import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Science as ScienceIcon,
  Layers as LayersIcon,
  InfoOutlined as InfoIcon,
  HelpOutline as HelpIcon,
  Balance as BalanceIcon,
  Bolt as BoltIcon,
  Grass as GrassIcon,
  LocalFlorist as FloristIcon,
  Grain as GrainIcon,
  Recycling as RecyclingIcon,
  SwapHorizontalCircle as SwapHorizontalCircleIcon,
} from '@mui/icons-material';
import { DeploymentMarkerData } from 'src/view/Project/InteractiveMap';
import clientService from 'src/clientService';
import { formatDate } from 'src/util/date-utils';

const CATEGORY_GUIDE = [
  {
    label: 'Optimal',
    words: 'optimal / good / normal',
    color: '#10b981',
    meaning: 'The laboratory reports this value inside the usable range for the parameter.',
  },
  {
    label: 'High / excessive',
    words: 'high / warning',
    color: '#f59e0b',
    meaning: 'The laboratory reports this value above the usable range for the parameter.',
  },
  {
    label: 'Low / deficient',
    words: 'low / error / critical',
    color: '#ef4444',
    meaning: 'The laboratory reports this value below the usable range for the parameter.',
  },
];

const TIER_COLUMNS = ['Very Low', 'Low', 'Medium', 'High', 'Very High'];
// Empty tier = not defined for that parameter, never a guessed value.
const RANGE_TIERS = [
  { parameter: 'Nitrate Nitrogen', unit: 'ppm', tiers: ['<10', '10-20', '20-30', '30-40', '>40'] },
  { parameter: 'Bray-P', unit: 'ppm', tiers: ['<20', '20-40', '40-80', '80-120', '>120'] },
  { parameter: 'Olsen-P', unit: 'ppm', tiers: ['', '<4.46', '4.47-11', '>11', ''] },
  { parameter: 'Potassium', unit: 'ppm', tiers: ['<10', '10-15', '15-40', '40-100', '>100'] },
  { parameter: 'Organic Carbon', unit: '%', tiers: ['', '<0.51', '0.51-0.75', '>0.75', ''] },
];

const GUIDE_TABS = ['Categories', 'Reference ranges', 'Parameters'];

export interface SoilElementResult {
  result_id?: string;
  element?: {
    name?: string;
    symbol?: string;
    category?: string;
    default_unit?: string;
    description?: string;
  };
  value?: number | string;
  unit?: string;
  interpretation?: string;
}

export interface SoilSampleItem {
  sample_id?: string;
  sample_name?: string;
  collected_date?: string;
  soil_density?: number;
  depth?: number;
  status?: string;
  notes?: string;
  results?: SoilElementResult[];
}

export interface SoilSensorResultsPopupProps {
  open: boolean;
  onClose: () => void;
  siteName: string;
  deployment?: DeploymentMarkerData | null;
  deploymentId?: string | null;
  projectId?: string | null;
}

const SoilSensorResultsPopup: React.FC<SoilSensorResultsPopupProps> = ({
  open,
  onClose,
  siteName,
  deployment,
  deploymentId,
  projectId,
}) => {
  const [samples, setSamples] = useState<SoilSampleItem[]>([]);
  const [selectedSampleIndex, setSelectedSampleIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [guideOpen, setGuideOpen] = useState<boolean>(false);
  const [guideTab, setGuideTab] = useState<number>(0);

  const deploymentTitle = deployment?.deployment_name || 'Soil Sensor Unit';
  const normalizedSiteName = siteName || deployment?.site_name || 'Location Site';
  const deviceTypeStr = (deployment?.device_type || 'SOIL_SENSOR').toUpperCase();

  let locationRegion = '';
  const nameLower = normalizedSiteName.toLowerCase();
  if (nameLower.includes('dwarik')) {
    locationRegion = 'SUNDARBANS, WEST BENGAL';
  } else if (nameLower.includes('maipith') || nameLower.includes('maithpith')) {
    locationRegion = 'SUNDARBANS DELTA, WEST BENGAL';
  } else if (nameLower.includes('tiphli') || nameLower.includes('tipli') || nameLower.includes('tipl')) {
    locationRegion = 'SUNDARBANS FRONTIER, WEST BENGAL';
  } else if (nameLower.includes('masira') || nameLower.includes('karpa') || nameLower.includes('amanar')) {
    locationRegion = 'MADHYA PRADESH';
  } else if (nameLower.includes('pipri')) {
    locationRegion = 'UTTAR PRADESH';
  }

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    const dId = deploymentId || deployment?.deployment_id;

    const fetchSoilData = async () => {
      let fetchedSamples: SoilSampleItem[] = [];

      if (dId) {
        // Try deployment-specific endpoint first
        try {
          const res = await clientService.request<any, any>({
            path: `/api/v1/soil-chemistry/deployments/${dId}/samples`,
            method: 'GET',
            format: 'json',
            secure: true,
          });
          const listData = res.data?.data || (Array.isArray(res.data) ? res.data : null);
          if (listData && Array.isArray(listData) && listData.length > 0) {
            fetchedSamples = listData;
          }
        } catch (e) {
          // Secondary fallback endpoint query
          try {
            const res2 = await clientService.request<any, any>({
              path: `/api/v1/soil-chemistry/samples?deployment_id=${dId}`,
              method: 'GET',
              format: 'json',
              secure: true,
            });
            const listData2 = res2.data?.data || (Array.isArray(res2.data) ? res2.data : null);
            if (listData2 && Array.isArray(listData2) && listData2.length > 0) {
              fetchedSamples = listData2;
            }
          } catch (err) {
            // Ignore error
          }
        }
      }

      // If deployment yielded no samples, try project-wide endpoint if available
      if (fetchedSamples.length === 0 && projectId) {
        try {
          const resProj = await clientService.request<any, any>({
            path: `/api/v1/soil-chemistry/projects/${projectId}/samples`,
            method: 'GET',
            format: 'json',
            secure: true,
          });
          const projList = resProj.data?.data || (Array.isArray(resProj.data) ? resProj.data : null);
          if (projList && Array.isArray(projList) && projList.length > 0) {
            fetchedSamples = projList;
          }
        } catch (e) {
          // Ignore error
        }
      }

      setSamples(fetchedSamples);
      setSelectedSampleIndex(0);
      setLoading(false);
    };

    const timer = setTimeout(() => {
      void fetchSoilData();
    }, 200);

    return () => clearTimeout(timer);
  }, [open, deploymentId, deployment?.deployment_id, projectId]);

  const activeSample = samples[selectedSampleIndex] || samples[0];

  const guideParameters = (() => {
    const bySymbol = new Map<string, { symbol: string; name: string; description: string; unit: string }>();
    samples.forEach(sample => {
      (sample.results || []).forEach(result => {
        const symbol = result.element?.symbol || result.element?.name;
        if (!symbol || bySymbol.has(symbol)) return;
        bySymbol.set(symbol, {
          symbol,
          name: result.element?.name || symbol,
          description: result.element?.description || '',
          unit: result.unit || result.element?.default_unit || '',
        });
      });
    });
    return Array.from(bySymbol.values());
  })();

  // Helper for interpretation status chip color
  const getInterpretationColor = (interp?: string) => {
    if (!interp) return { bg: 'rgba(255, 255, 255, 0.08)', color: '#aaa', border: 'rgba(255, 255, 255, 0.15)' };
    const lower = interp.toLowerCase();
    if (lower.includes('optimal') || lower.includes('good') || lower.includes('normal')) {
      return { bg: 'rgba(16, 185, 129, 0.12)', color: '#10b981', border: 'rgba(16, 185, 129, 0.3)' };
    }
    if (lower.includes('high') || lower.includes('warning')) {
      return { bg: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', border: 'rgba(245, 158, 11, 0.3)' };
    }
    if (lower.includes('low') || lower.includes('error') || lower.includes('critical')) {
      return { bg: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', border: 'rgba(239, 68, 68, 0.3)' };
    }
    return { bg: 'rgba(16, 185, 129, 0.12)', color: '#10b981', border: 'rgba(16, 185, 129, 0.3)' };
  };

  const isPhParameter = (symbol?: string, name?: string) => {
    const key = `${symbol || ''} ${name || ''}`.toLowerCase();
    return /(^|[^a-z])ph([^a-z]|$)/.test(key) || /acidity|alkalinity/.test(key);
  };

  // The lab feed calls this parameter "pH (Water)"; the report calls it Soil pH Value.
  const parameterLabel = (name: string | undefined, symbol: string | undefined, withSymbol: boolean): string => {
    if (isPhParameter(symbol, name)) return 'Soil pH Value';
    const base = name || 'Element';
    return withSymbol && symbol ? `${base} (${symbol})` : base;
  };

  const getParameterIcon = (symbol?: string, name?: string) => {
    const key = `${symbol || ''} ${name || ''}`.toLowerCase();
    if (isPhParameter(symbol, name)) return BalanceIcon;
    if (/\bec\b/.test(key) || /conductivity/.test(key)) return BoltIcon;
    if (/\bcec\b/.test(key) || /exchange/.test(key)) return SwapHorizontalCircleIcon;
    if (/\bsoc\b/.test(key) || /organic|carbon/.test(key)) return RecyclingIcon;
    if (/density|bulk|compaction|texture|sand|silt|clay/.test(key)) return LayersIcon;
    if (/nitrogen|\bn\b/.test(key)) return GrassIcon;
    if (/phosphor|\bp\b/.test(key)) return FloristIcon;
    if (/potassium|\bk\b/.test(key)) return GrainIcon;
    return ScienceIcon;
  };

  // Compute summary stats across samples if available
  const totalSamples = samples.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #090E0B 0%, #030403 100%)',
          border: '1.5px solid #142218',
          borderRadius: '20px',
          color: '#fff',
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
          overflow: 'hidden',
          p: 2.5,
          boxShadow: '0 32px 80px rgba(0, 0, 0, 0.85)',
        },
      }}
    >
      {/* Header bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <GrassIcon sx={{ color: '#10b981', fontSize: 22 }} />
            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#fff', letterSpacing: '0.5px' }}>
              {deploymentTitle}
            </Typography>
            <Chip
              label={deviceTypeStr}
              size="small"
              sx={{
                background: 'rgba(16, 185, 129, 0.12)',
                color: '#10b981',
                fontWeight: 800,
                fontSize: '9px',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                height: '20px',
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.55)', fontWeight: 600 }}>
            Site: <span style={{ color: '#fff' }}>{normalizedSiteName}</span>
            {locationRegion ? ` • ${locationRegion}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.4)', p: 0.5, '&:hover': { color: '#fff' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {/* Samples collected + link to the parameter guide */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 1.25,
              bgcolor: 'rgba(255, 255, 255, 0.02)',
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.04)',
            }}
          >
            <ScienceIcon sx={{ color: '#10b981', fontSize: 16 }} />
            <Typography
              sx={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.35)', fontWeight: 700, letterSpacing: '1px' }}
            >
              SAMPLES COLLECTED
            </Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#fff' }}>{totalSamples}</Typography>
          </Box>
          <Button
            size="small"
            startIcon={<InfoIcon sx={{ fontSize: 15 }} />}
            onClick={() => {
              setGuideTab(0);
              setGuideOpen(true);
            }}
            sx={{
              color: 'rgba(255, 255, 255, 0.65)',
              borderColor: 'rgba(16, 185, 129, 0.4)',
              fontWeight: 700,
              fontSize: '10px',
              letterSpacing: '0.5px',
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': { borderColor: '#10b981', bgcolor: 'rgba(16, 185, 129, 0.08)', color: '#fff' },
            }}
          >
            SOIL PARAMETER GUIDE
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: '#10b981' }} />
            <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', mt: 1.5, fontWeight: 600 }}>
              Loading soil sample results...
            </Typography>
          </Box>
        ) : samples.length === 0 ? (
          /* Empty State UI when no soil sample data exists */
          <Box
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.02)',
              borderRadius: '16px',
              p: 5,
              border: '1px solid rgba(255, 255, 255, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              my: 1,
            }}
          >
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                bgcolor: 'rgba(16, 185, 129, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                border: '1px solid rgba(16, 185, 129, 0.25)',
              }}
            >
              <ScienceIcon sx={{ color: '#10b981', fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#fff', mb: 0.75 }}>
              No Soil Sample Data Available
            </Typography>
            <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', maxWidth: '400px', lineHeight: 1.5 }}>
              There are currently no laboratory soil sample records or chemical measurement data uploaded for this deployment unit ({deploymentTitle}).
            </Typography>
          </Box>
        ) : (
          <>
            {/* Sample Tabs / Selector Bar */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)', fontWeight: 700, letterSpacing: '0.5px' }}>
                SELECT SOIL SAMPLE RECORD
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                {samples.map((sample, idx) => {
                  const isSelected = selectedSampleIndex === idx;
                  return (
                    <Box
                      key={sample.sample_id || idx}
                      onClick={() => setSelectedSampleIndex(idx)}
                      sx={{
                        p: '8px 14px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'rgba(16, 185, 129, 0.12)' : 'rgba(255, 255, 255, 0.02)',
                        border: isSelected ? '1px solid #10b981' : '1px solid rgba(255, 255, 255, 0.06)',
                        color: isSelected ? '#10b981' : 'rgba(255, 255, 255, 0.7)',
                        fontWeight: 700,
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: isSelected ? 'rgba(16, 185, 129, 0.18)' : 'rgba(255, 255, 255, 0.05)',
                          borderColor: isSelected ? '#10b981' : 'rgba(16, 185, 129, 0.4)',
                        },
                      }}
                    >
                      {sample.sample_name || `Sample #${idx + 1}`}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Active Sample Overview */}
            {activeSample && (
              <Box
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '12px',
                  p: 2,
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {/* Metadata Row */}
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 600 }}>
                      COLLECTED DATE
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff', mt: 0.25 }}>
                      {activeSample.collected_date ? formatDate(activeSample.collected_date) : 'N/A'}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 600 }}>
                      SOIL DENSITY
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff', mt: 0.25 }}>
                      {activeSample.soil_density !== undefined && activeSample.soil_density !== null
                        ? `${activeSample.soil_density} g/cm³`
                        : '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 600 }}>
                      SAMPLING DEPTH
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff', mt: 0.25 }}>
                      {activeSample.depth !== undefined && activeSample.depth !== null
                        ? `${activeSample.depth} cm`
                        : '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 600 }}>
                      STATUS
                    </Typography>
                    <Chip
                      label={activeSample.status || 'COMPLETED'}
                      size="small"
                      sx={{
                        mt: 0.25,
                        height: '18px',
                        fontSize: '9px',
                        fontWeight: 800,
                        bgcolor: 'rgba(16, 185, 129, 0.15)',
                        color: '#10b981',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Laboratory Chemical Element Measurements */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      sx={{ fontSize: '11px', color: '#10b981', fontWeight: 800, letterSpacing: '0.5px' }}
                    >
                      LABORATORY CHEMICAL MEASUREMENTS
                    </Typography>
                    <IconButton
                      aria-label="Soil parameter reference ranges"
                      onClick={() => {
                        setGuideTab(1);
                        setGuideOpen(true);
                      }}
                      sx={{
                        p: 0.25,
                        color: 'rgba(255, 255, 255, 0.4)',
                        '&:hover': { color: '#10b981', bgcolor: 'rgba(16, 185, 129, 0.1)' },
                      }}
                    >
                      <HelpIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>

                  {!activeSample.results || activeSample.results.length === 0 ? (
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.4)', fontStyle: 'italic' }}>
                      No chemical measurements recorded for this sample.
                    </Typography>
                  ) : (
                    <Grid container spacing={1.25} sx={{ maxHeight: '220px', overflowY: 'auto', pr: 0.5 }}>
                      {activeSample.results.map((res, idx) => {
                        const style = getInterpretationColor(res.interpretation);
                        const ParameterIcon = getParameterIcon(res.element?.symbol, res.element?.name);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={res.result_id || idx}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.25,
                                bgcolor: 'rgba(255, 255, 255, 0.02)',
                                border: '1px solid rgba(255, 255, 255, 0.06)',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
                                  <ParameterIcon sx={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: 14, flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#fff' }}>
                                    {parameterLabel(res.element?.name, res.element?.symbol, true)}
                                  </Typography>
                                </Box>
                                {res.interpretation && (
                                  <Chip
                                    label={res.interpretation}
                                    size="small"
                                    sx={{
                                      height: '16px',
                                      fontSize: '8px',
                                      fontWeight: 800,
                                      bgcolor: style.bg,
                                      color: style.color,
                                      border: `1px solid ${style.border}`,
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography sx={{ fontSize: '15px', fontWeight: 800, color: '#10b981' }}>
                                {res.value} <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{res.unit}</span>
                              </Typography>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Box>

                {/* Sample Notes */}
                {activeSample.notes && (
                  <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', p: 1.25, borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.04)' }}>
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.4)', fontWeight: 700, mb: 0.25 }}>
                      SAMPLE NOTES
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.75)', lineHeight: 1.4 }}>
                      {activeSample.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* Soil parameter guide */}
      <Dialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0c1210',
            border: '1px solid #1b2623',
            borderRadius: '14px',
            color: '#f2f5f4',
            fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
            maxWidth: '700px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2, pb: 1.5 }}>
          <Typography sx={{ fontSize: '21px', fontWeight: 800 }}>Soil parameter guide</Typography>
          <IconButton onClick={() => setGuideOpen(false)} sx={{ color: '#7d8c87', p: 0.5, '&:hover': { color: '#fff' } }}>
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: '22px', px: 2.5, borderBottom: '1px solid #1b2623' }}>
          {GUIDE_TABS.map((label, idx) => (
            <Box
              key={label}
              component="button"
              onClick={() => setGuideTab(idx)}
              sx={{
                appearance: 'none',
                background: 'none',
                border: 0,
                borderBottom: `2px solid ${guideTab === idx ? '#10b981' : 'transparent'}`,
                color: guideTab === idx ? '#10b981' : '#7d8c87',
                fontFamily: 'inherit',
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                py: 1.25,
                px: 0,
                cursor: 'pointer',
              }}
            >
              {label}
            </Box>
          ))}
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 0, pb: 2.5, overflowY: 'auto' }}>
          {guideTab === 0 && (
            <Box>
              <Typography sx={{ fontSize: '13px', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#10b981', fontWeight: 800, mt: 1.75, mb: 0.5 }}>
                What the categories mean
              </Typography>
              {CATEGORY_GUIDE.map((cat) => (
                <Box
                  key={cat.label}
                  sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start', py: 1.5, borderBottom: '1px solid #1b2623', '&:last-child': { borderBottom: 0 } }}
                >
                  <Chip
                    label={cat.label}
                    size="small"
                    sx={{
                      width: '140px',
                      flexShrink: 0,
                      height: 'auto',
                      py: 0.5,
                      borderRadius: '999px',
                      fontSize: '13px',
                      fontWeight: 600,
                      bgcolor: `${cat.color}1a`,
                      color: cat.color,
                      border: `1px solid ${cat.color}66`,
                    }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: '15px', lineHeight: 1.5 }}>{cat.meaning}</Typography>
                    <Typography sx={{ fontSize: '11.5px', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#7d8c87', mt: 0.4 }}>
                      Labels coloured: {cat.words}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {guideTab === 1 && (
            <Box>
              <Typography sx={{ fontSize: '13px', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#10b981', fontWeight: 800, mt: 1.75, mb: 1.25 }}>
                Published reference ranges
              </Typography>
              <Box sx={{ border: '1px solid #1b2623', borderRadius: '10px', bgcolor: '#0f1613', overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: '600px' }}>
                    <Box component="thead">
                      <Box component="tr">
                        <Box component="th" sx={{ fontSize: '11.5px', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#7d8c87', fontWeight: 500, textAlign: 'left', pl: 1.75, pr: 1, py: 1.25, borderBottom: '1px solid #1b2623' }}>
                          Parameter
                        </Box>
                        {TIER_COLUMNS.map((tier) => (
                          <Box key={tier} component="th" sx={{ fontSize: '11.5px', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#7d8c87', fontWeight: 500, textAlign: 'center', px: 1, py: 1.25, borderBottom: '1px solid #1b2623' }}>
                            {tier}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {RANGE_TIERS.map((row) => (
                        <Box key={row.parameter} component="tr">
                          <Box component="td" sx={{ textAlign: 'left', fontWeight: 600, fontSize: '15.5px', pl: 1.75, pr: 1, py: 1.5, borderBottom: '1px solid #1b2623', whiteSpace: 'nowrap' }}>
                            {row.parameter}
                            <Typography component="span" sx={{ display: 'block', fontWeight: 400, fontSize: '12.5px', color: '#7d8c87' }}>
                              {row.unit}
                            </Typography>
                          </Box>
                          {row.tiers.map((tier, idx) => (
                            <Box
                              key={TIER_COLUMNS[idx]}
                              component="td"
                              sx={{ textAlign: 'center', fontSize: '15.5px', px: 1, py: 1.5, borderBottom: '1px solid #1b2623', whiteSpace: 'nowrap', color: tier ? '#f2f5f4' : '#33423d', fontWeight: tier ? 600 : 400 }}
                            >
                              {tier || '—'}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mt: 2 }}>
                <InfoIcon sx={{ color: '#10b981', fontSize: 20, flexShrink: 0, mt: '3px' }} />
                <Typography sx={{ fontSize: '15.5px', color: '#7d8c87', lineHeight: 1.5 }}>
                  <Box component="span" sx={{ color: '#f2f5f4', fontWeight: 600 }}>pH and EC:</Box> the tier depends upon the crop being grown.
                </Typography>
              </Box>
            </Box>
          )}

          {guideTab === 2 && (
            <Box>
              <Typography sx={{ fontSize: '13px', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#10b981', fontWeight: 800, mt: 1.75, mb: 0.5 }}>
                What each parameter measures
              </Typography>
              {guideParameters.length === 0 ? (
                <Typography sx={{ fontSize: '14px', color: '#7d8c87', lineHeight: 1.5 }}>
                  Parameters appear here once a laboratory result is recorded for this deployment.
                </Typography>
              ) : (
                guideParameters.map((param) => (
                  <Box
                    key={param.symbol}
                    sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1.25, borderBottom: '1px solid #1b2623', '&:last-child': { borderBottom: 0 } }}
                  >
                    <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: '8px', border: '1px solid #1b2623', bgcolor: '#0f1613', color: '#10b981', display: 'grid', placeItems: 'center', fontSize: '18px', fontWeight: 700, fontStyle: 'italic' }}>
                      {param.symbol}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
                        {parameterLabel(param.name, param.symbol, false)}
                        {param.unit ? ` (${param.unit})` : ''}
                      </Typography>
                      {param.description && (
                        <Typography sx={{ fontSize: '14px', color: '#7d8c87', lineHeight: 1.45 }}>{param.description}</Typography>
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default SoilSensorResultsPopup;
